import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaWallet,
  FaPlus,
  FaMinus,
  FaArrowUp,
  FaArrowDown,
  FaSync,
  FaArrowLeft,
  FaShieldAlt,
  FaCar,
  FaCheckCircle,
  FaUniversity,
  FaBolt,
  FaHistory,
  FaRegCreditCard
} from "react-icons/fa";
import {
  getUserWallet,
  getUserTransactions,
  getBankDetails,
  reset,
} from "../features/wallet/walletSlice";
import AddMoneyModal from "../components/Wallet/AddMoneyModal";
import WithdrawMoneyModal from "../components/Wallet/WithdrawMoneyModal";

const UserWallet = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { wallet, transactions, isLoading, isError, message, isSuccess } = useSelector(
    (state) => state.wallet
  );

  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState("all"); // 'all', 'credit', 'debit', 'withdrawal'

  const corporateToken = localStorage.getItem("corporateToken");

  useEffect(() => {
    dispatch(getUserWallet());
    dispatch(getUserTransactions({ page: currentPage }));
    dispatch(getBankDetails());
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(reset());
    }
    if (isSuccess && message && message.toLowerCase().includes("successfully")) {
      toast.success(message);
      dispatch(getUserWallet());
      dispatch(getUserTransactions({ page: 1 }));
      setCurrentPage(1);
      dispatch(reset());
    }
  }, [isError, isSuccess, message, dispatch]);

  const handleRefresh = () => {
    dispatch(getUserWallet());
    dispatch(getUserTransactions({ page: 1 }));
    setCurrentPage(1);
    toast.info("Wallet refreshed");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isTransactionCredit = (transaction) => {
    if (typeof transaction.isCredit !== "undefined") {
      return Boolean(transaction.isCredit);
    }
    const creditTypes = ["wallet_topup", "refund", "wallet_refund", "bonus_credit", "adjustment"];
    if (creditTypes.includes(transaction.type) || (transaction.type && transaction.type.toLowerCase().includes("refund"))) {
      return true;
    }
    return false;
  };

  // Filter transactions based on selected tab
  const filteredTransactions = useMemo(() => {
    if (!transactions || !Array.isArray(transactions)) return [];
    if (filterType === "all") return transactions;
    if (filterType === "credit") {
      return transactions.filter((t) => isTransactionCredit(t));
    }
    if (filterType === "debit") {
      return transactions.filter((t) => !isTransactionCredit(t) && t.type !== "withdrawal");
    }
    if (filterType === "withdrawal") {
      return transactions.filter((t) => t.type === "withdrawal");
    }
    return transactions;
  }, [transactions, filterType]);

  const getTransactionIcon = (type, isCredit) => {
    if (type === "withdrawal") {
      return (
        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
          <FaUniversity size={15} />
        </div>
      );
    }
    if (isCredit) {
      return (
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
          <FaArrowDown size={14} />
        </div>
      );
    }
    if (type === "ride_payment" || type === "ride_booking" || (type && type.includes("ride"))) {
      return (
        <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
          <FaCar size={15} />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
        <FaArrowUp size={14} />
      </div>
    );
  };

  const getStatusBadge = (status, type) => {
    if (type !== "withdrawal") return null;

    const statusConfig = {
      completed: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Transferred" },
      pending: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "Processing" },
      rejected: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Declined" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`${config.bg} ${config.text} ${config.border} border px-2.5 py-0.5 rounded-full text-[11px] font-bold`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-16 font-sans">
      {/* MOBILE STICKY HEADER */}
      <div className={`md:hidden sticky top-0 left-0 right-0 z-50 text-white shadow-md border-b ${
        corporateToken
          ? "bg-gradient-to-r from-red-800 to-rose-900 border-red-700"
          : "bg-gradient-to-r from-blue-900 to-indigo-900 border-blue-800"
      }`}>
        <div className="flex items-center px-4 py-3 gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-white hover:text-amber-300 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-base leading-tight truncate">My Wallet</h1>
            <p className="text-xs text-blue-200 truncate">CabZigo Cashless Ride Balance</p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="text-white hover:text-amber-300 p-2 rounded-lg bg-white/15"
            title="Refresh balance"
          >
            <FaSync size={13} className={isLoading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* HERO BANNER SECTION (CabZigo Theme Color) */}
      <div className={`relative text-white overflow-hidden border-b ${
        corporateToken
          ? "bg-gradient-to-r from-[#F56753] via-red-700 to-rose-900 border-red-600 shadow-md"
          : "bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 border-blue-700 shadow-md"
      }`}>
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-10 left-10 w-72 h-72 bg-amber-400/15 rounded-full blur-2xl pointer-events-none"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {/* Top navigation / Back link */}
          <div className="hidden md:flex justify-between items-center mb-6">
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}
              className="inline-flex items-center gap-2 text-white hover:text-white text-xs font-semibold bg-white/15 hover:bg-white/25 backdrop-blur-md px-3.5 py-1.5 rounded-full transition-all border border-white/20 shadow-sm"
            >
              <FaArrowLeft size={11} />
              <span>Back to Dashboard</span>
            </button>
            <div className="flex items-center gap-3 text-xs text-blue-100">
              <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 text-emerald-300 font-semibold px-2.5 py-1 rounded-full border border-emerald-400/30">
                <FaShieldAlt size={11} /> 100% Secure Payments
              </span>
              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex items-center gap-1.5 text-white hover:text-white bg-white/15 hover:bg-white/25 px-3 py-1 rounded-full transition-colors border border-white/20"
                title="Refresh Wallet Balance"
              >
                <FaSync size={11} className={isLoading ? "animate-spin" : ""} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* DIGITAL WALLET CARD (Fintech Card Style - CabZigo Theme) */}
          <div className={`border border-white/20 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl relative overflow-hidden ${
            corporateToken
              ? "bg-gradient-to-br from-red-950/95 via-red-900/95 to-slate-950/95"
              : "bg-gradient-to-br from-blue-900/95 via-blue-800/95 to-indigo-950/95"
          }`}>
            {/* Background Chip & Watermark */}
            <div className="absolute top-6 right-6 flex items-center gap-2 opacity-90">
              <div className="text-right">
                <span className="text-[10px] uppercase tracking-widest text-blue-200 font-bold block">CabZigo</span>
                <span className="text-xs font-bold text-amber-300">Smart Wallet</span>
              </div>
              <div className="w-10 h-8 rounded-lg bg-gradient-to-tr from-amber-400 to-yellow-200 border border-yellow-300/40 opacity-95 shadow-sm flex items-center justify-center">
                <FaRegCreditCard className={corporateToken ? "text-red-950" : "text-blue-950"} size={16} />
              </div>
            </div>

            <div className="max-w-md">
              <span className="text-xs uppercase font-bold tracking-wider text-blue-200 block mb-1">
                Total Available Balance
              </span>
              <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight flex items-baseline gap-1">
                {formatCurrency(wallet?.balance || 0)}
              </h2>

              {/* Breakdown Row */}
              <div className="grid grid-cols-2 gap-3 mt-6 pt-5 border-t border-white/15">
                <div className="bg-white/10 border border-white/15 rounded-2xl p-3">
                  <span className="text-[11px] font-semibold text-blue-200 uppercase tracking-wide block">
                    Withdrawable Balance
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-white block mt-0.5">
                    {formatCurrency(wallet?.withdrawableBalance || 0)}
                  </span>
                  <span className="text-[10px] text-emerald-300 font-medium block mt-0.5">
                    Ready to transfer to bank
                  </span>
                </div>

                <div className="bg-white/10 border border-white/15 rounded-2xl p-3">
                  <span className="text-[11px] font-semibold text-blue-200 uppercase tracking-wide block">
                    Ride Credits / Offers
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-white block mt-0.5">
                    {formatCurrency(Math.max(0, (wallet?.balance || 0) - (wallet?.withdrawableBalance || 0)))}
                  </span>
                  <span className="text-[10px] text-amber-300 font-medium block mt-0.5">
                    Applicable on upcoming rides
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddMoneyModal(true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/25 transition-all hover:scale-[1.02] text-sm"
                >
                  <FaPlus size={12} />
                  <span>Add Money</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-6 py-3 rounded-xl shadow-lg shadow-orange-500/25 transition-all hover:scale-[1.02] text-sm"
                >
                  <FaMinus size={12} />
                  <span>Withdraw to Bank</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QUICK VALUE PROPOSITIONS (3-Cards) */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200/80 flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
              <FaBolt size={16} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">1-Click Booking</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">Instant ride payment without OTP delays</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200/80 flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
              <FaCheckCircle size={16} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">Instant Refunds</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">Cancelled ride refunds credited immediately</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200/80 flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <FaUniversity size={16} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">Easy Withdrawals</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">Transfer your funds back to bank anytime</p>
            </div>
          </div>
        </div>
      </div>

      {/* TRANSACTIONS SECTION */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5 sm:p-7">
          {/* Header & Tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 border-b border-slate-100 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <FaHistory className="text-slate-400" size={15} />
                <h3 className="text-lg font-bold text-slate-900">Wallet Passbook</h3>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Detailed transaction records of all top-ups, ride fares, and refunds
              </p>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-1.5 bg-slate-100/80 p-1 rounded-xl self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setFilterType("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  filterType === "all"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setFilterType("credit")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  filterType === "credit"
                    ? "bg-white text-emerald-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Money Added (+)
              </button>
              <button
                type="button"
                onClick={() => setFilterType("debit")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  filterType === "debit"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Rides (-)
              </button>
              <button
                type="button"
                onClick={() => setFilterType("withdrawal")}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  filterType === "withdrawal"
                    ? "bg-white text-amber-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Withdrawals
              </button>
            </div>
          </div>

          {/* Transactions List */}
          <div className="mt-5">
            {isLoading && (!transactions || transactions.length === 0) ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-xs font-semibold text-slate-500">Loading transactions...</p>
              </div>
            ) : filteredTransactions && filteredTransactions.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {filteredTransactions.map((transaction, index) => {
                  const isCredit = isTransactionCredit(transaction);
                  const isWithdrawal = transaction.type === "withdrawal";

                  return (
                    <div
                      key={transaction._id || index}
                      className="py-3.5 sm:py-4 flex items-center justify-between gap-4 hover:bg-slate-50/70 rounded-xl px-2.5 transition-colors"
                    >
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        {getTransactionIcon(transaction.type, isCredit)}
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 text-xs sm:text-sm truncate">
                            {transaction.description || (isWithdrawal ? "Bank Account Withdrawal" : isCredit ? "Wallet Top-up" : "Ride Fare Deduction")}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400 flex-wrap">
                            <span>{formatDate(transaction.date || transaction.createdAt)}</span>
                            {transaction.referenceId && (
                              <>
                                <span>&bull;</span>
                                <span className="font-mono text-slate-400 truncate max-w-[140px]">
                                  Ref: {transaction.referenceId}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                        <span
                          className={`text-sm sm:text-base font-extrabold ${
                            isCredit ? "text-emerald-600" : isWithdrawal ? "text-amber-600" : "text-slate-900"
                          }`}
                        >
                          {isCredit ? "+" : isWithdrawal ? "-" : "-"}
                          {formatCurrency(Math.abs(transaction.amount))}
                        </span>
                        {getStatusBadge(transaction.status, transaction.type)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* EMPTY STATE */
              <div className="text-center py-12 px-4 max-w-md mx-auto">
                <div className="w-16 h-16 rounded-3xl bg-orange-50 text-orange-500 flex items-center justify-center mx-auto mb-4 shadow-sm border border-orange-100">
                  <FaWallet size={26} />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-1">
                  {filterType === "all" ? "No Transactions Yet" : `No ${filterType} transactions found`}
                </h4>
                <p className="text-xs text-slate-500 leading-relaxed mb-6">
                  {filterType === "all"
                    ? "Top up your wallet to enjoy instant 1-click ride bookings without entering card or UPI details each time."
                    : `There are currently no transactions matching the "${filterType}" filter.`}
                </p>
                <button
                  type="button"
                  onClick={() => setShowAddMoneyModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2.5 rounded-xl shadow-md shadow-emerald-500/20 text-xs transition-all hover:scale-[1.02]"
                >
                  <FaPlus size={11} />
                  <span>Add Money to Get Started</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODALS */}
      <AddMoneyModal
        isOpen={showAddMoneyModal}
        onClose={() => setShowAddMoneyModal(false)}
        currentBalance={wallet?.balance || 0}
      />

      <WithdrawMoneyModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        maxAmount={wallet?.withdrawableBalance || 0}
      />
    </div>
  );
};

export default UserWallet;
